import { proxyCustomElement, HTMLElement, h } from '@stencil/core/internal/client';
import { K as KeyType } from './key-type.js';

const onvioPopoverCss = ".popover-tooltip{background-color:#FFFFFF;border:1px solid #737373;border-radius:8px;box-shadow:inset 0 0 4px rgba(0, 0, 0, 0.2);color:#000000;font-family:\"Source Sans Pro\", arial, sans-serif;font-size:14px;line-height:1.5;padding:8px;position:absolute;transform:translateX(-50%);white-space:pre-wrap;word-break:break-all;z-index:1}.popover-panel-tooltip{margin-top:16px;width:318.41px}.popover-thumbnail-tooltip{bottom:0;left:50%;right:0;width:156.4px}.popover-container{display:flex;overflow:hidden !important;overflow-wrap:hidden;word-break:break-all}.popover-panel-container{justify-content:left}.popover-thumbnail-container{justify-content:center;padding:0}.popover-text{display:inline-block;overflow:hidden;text-overflow:hidden}.onvio-ellipsis{align-self:flex-end;cursor:pointer;display:inline-block;outline:none}.onvio-ellipsis:hover,.onvio-ellipsis:focus{color:#005da2}@media only screen and (min-width: 320px) and (max-width: 580px){.popover-panel-tooltip{left:50%;max-width:100vw;width:80%}}@media only screen and (min-width: 689px){.popover-panel-tooltip{margin-left:372px}}@media only screen and (min-width: 1440px){.popover-panel-tooltip{margin-left:569px}.popover-thumbnail-tooltip{bottom:25%;width:230.4px}}@media only screen and (min-width: 1920px){.popover-panel-tooltip{margin-left:910px}}@media only screen and (min-width: 580px) and (max-width: 689px){.popover-panel-tooltip{left:65%;max-width:100vw;right:0}}";

const OnvioPopover$1 = /*@__PURE__*/ proxyCustomElement(class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.__attachShadow();
    this.bottom = '';
    this.passwordHeight = '22';
    this.handleTooltipClick = (event) => {
      // Prevent the click event from propagating to the document
      event.stopPropagation();
    };
    this.handleKeyDown = (event) => {
      if (event.key === KeyType.SPACE) {
        const ellipsisButton = event.target;
        if (ellipsisButton instanceof OnvioPopover$1)
          event.preventDefault(); // Prevent default behavior (e.g., scrolling page)
      }
      if (event.key === KeyType.ESCAPE) {
        this.isTooltipVisible = false;
      }
    };
    this.handleEllipsisKeyUp = (event) => {
      event.preventDefault(); // Prevent default behavior (e.g., scrolling page)
      if (event.key === KeyType.ENTER || event.key === KeyType.SPACE) {
        this.isTooltipVisible = !this.isTooltipVisible;
      }
    };
    this.handleOutsideFocus = (event) => {
      var _a, _b;
      const target = event.target;
      const tooltipElement = (_a = this.hostElement.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('.popover-tooltip');
      // Check if the clicked element is not inside the popover container or the ellipsis button
      if (!((_b = target.shadowRoot) === null || _b === void 0 ? void 0 : _b.contains(tooltipElement))) {
        this.isTooltipVisible = false;
      }
    };
    this.text = '';
    this.height = '';
    this.width = '';
    this.alignRight = false;
    this.ellipsisAriaLabel = '';
    this.ellipsisDataQeId = '';
    this.passwordProtected = false;
    this.isTooltipVisible = false;
    this.isOverflowing = false;
  }
  watchPasswordProtected(newValue) {
    if (newValue) {
      requestAnimationFrame(() => {
        this.updateHeightAndOverflow();
      });
    }
  }
  componentDidLoad() {
    const elementToObserve = document.getElementById('pop-up-container');
    const observer = new ResizeObserver(() => {
      this.calculateOverflow();
    });
    if (elementToObserve) {
      observer.observe(elementToObserve);
    }
    this.calculateOverflow();
    document.addEventListener('click', this.handleOutsideFocus, true);
  }
  updateHeightAndOverflow() {
    this.calculateHeight();
    this.calculateOverflow();
  }
  calculateHeight() {
    if (this.passwordProtected) {
      this.height = this.passwordHeight; // Update height to 22px when passwordProtected is true
    }
  }
  calculateOverflow() {
    if (this.textElement) {
      const isOverflowing = this.textElement.scrollWidth > this.textElement.clientWidth ||
        this.textElement.scrollHeight > this.textElement.clientHeight + 1;
      this.isOverflowing = isOverflowing;
    }
  }
  disconnectedCallback() {
    var _a;
    document.removeEventListener('click', this.handleOutsideFocus);
    document.removeEventListener('keydown', this.handleKeyDown);
    const tooltipElement = (_a = this.hostElement.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('.popover-tooltip');
    if (tooltipElement) {
      tooltipElement.removeEventListener('click', this.handleTooltipClick);
    }
  }
  connectedCallback() {
    var _a;
    document.addEventListener('click', this.handleOutsideFocus, true);
    document.addEventListener('keydown', this.handleKeyDown);
    if (this.isTooltipVisible) {
      const tooltipElement = (_a = this.hostElement.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('.popover-tooltip');
      if (tooltipElement) {
        tooltipElement.addEventListener('click', this.handleTooltipClick);
      }
    }
  }
  handleClick() {
    this.isTooltipVisible = !this.isTooltipVisible;
  }
  render() {
    if (this.alignRight) {
      this.bottom = 'auto';
    }
    else {
      this.bottom = this.height === '59' ? '15%' : '20%';
    }
    return (h("slot", null, h("div", { class: `popover-container ${this.alignRight ? 'popover-panel-container' : 'popover-thumbnail-container'}`, style: { height: this.height + 'px', maxWidth: this.width + 'px' } }, h("div", { ref: el => {
        this.textElement = el;
      }, class: "popover-text" }, this.text), this.isOverflowing && (h("span", { onClick: () => this.handleClick(), class: `onvio-ellipsis`, tabindex: "0", role: "button", "aria-label": `${this.ellipsisAriaLabel}`, onKeyUp: this.handleEllipsisKeyUp, "data-qe-id": this.ellipsisDataQeId, "aria-controls": "popover-tooltip", "aria-describedby": "popover-tooltip", "aria-expanded": this.isTooltipVisible ? 'true' : 'false' }, "..."))), this.isTooltipVisible && (h("div", { class: `popover-tooltip ${this.alignRight ? 'popover-panel-tooltip' : 'popover-thumbnail-tooltip'}`, id: "popover-tooltip", "aria-hidden": !this.isTooltipVisible, "aria-live": "polite" }, this.text))));
  }
  get hostElement() { return this; }
  static get watchers() { return {
    "passwordProtected": ["watchPasswordProtected"]
  }; }
  static get style() { return onvioPopoverCss; }
}, [1, "onvio-popover", {
    "text": [1025],
    "height": [1025],
    "width": [1025],
    "alignRight": [1028, "align-right"],
    "ellipsisAriaLabel": [1, "ellipsis-aria-label"],
    "ellipsisDataQeId": [1, "ellipsis-data-qe-id"],
    "passwordProtected": [4, "password-protected"],
    "isTooltipVisible": [32],
    "isOverflowing": [32]
  }]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["onvio-popover"];
  components.forEach(tagName => { switch (tagName) {
    case "onvio-popover":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, OnvioPopover$1);
      }
      break;
  } });
}

const OnvioPopover = OnvioPopover$1;
const defineCustomElement = defineCustomElement$1;

export { OnvioPopover, defineCustomElement };

